/**
 * Config for source-specific "linked entity" dropdowns.
 * Used in NewEnquiry + EnquiryDetail edit mode.
 *
 * For each source that has an associated entity (referring doctor, ambulance
 * provider, corporate account, camp, marketing campaign, staff), this maps:
 *   - endpoint  → which API list to fetch options from
 *   - detailsKey → the key inside Enquiry.source_details JSON where the
 *                  selected id is stored
 *   - label     → shown next to the dropdown
 *   - mapOption → transforms an API row into {id, label} for the <option>
 *   - optional  → true when the enquiry can be saved without a selection
 */
export const SOURCE_LINK_CONFIG = {
  staff_referral: {
    label: 'Referring staff member',
    endpoint: 'auth/users/simple/',
    detailsKey: 'staff_id',
    mapOption: (u) => ({
      id: u.id,
      label: `${u.login_fname || u.first_name || ''} ${u.login_lname || u.last_name || ''}`.trim()
             || u.login_name || u.user_name || `Staff #${u.id}`,
    }),
  },
  doctor_referral: {
    label: 'Referring doctor',
    endpoint: 'engage/referring-doctors/',
    detailsKey: 'referring_doctor_id',
    mapOption: (d) => ({
      id: d.id,
      label: `${d.name}${d.specialty ? ` — ${d.specialty}` : ''}${d.city ? ` (${d.city})` : ''}`,
    }),
  },
  ambulance_referral: {
    label: 'Ambulance provider',
    endpoint: 'engage/ambulance-providers/',
    detailsKey: 'ambulance_provider_id',
    mapOption: (a) => ({
      id: a.id,
      label: `${a.name}${a.vehicle_number ? ` — ${a.vehicle_number}` : ''}`,
    }),
  },
  corporate_account: {
    label: 'Corporate account',
    endpoint: 'engage/corporate-accounts/',
    detailsKey: 'corporate_account_id',
    mapOption: (c) => ({
      id: c.id,
      label: c.company_name || `Corporate #${c.id}`,
    }),
  },
  health_camp: {
    label: 'Health camp',
    endpoint: 'engage/health-camps/',
    detailsKey: 'health_camp_id',
    mapOption: (c) => ({
      id: c.id,
      label: c.name || `Camp #${c.id}`,
    }),
  },
  digital_marketing: {
    label: 'Marketing campaign',
    // Staff often don't know which campaign a caller saw; an enquiry without
    // one is reported as unattributed digital rather than blocked.
    optional: true,
    endpoint: 'engage/digital-marketing-campaigns/',
    detailsKey: 'campaign_id',
    mapOption: (c) => ({
      id: c.id,
      label: `${c.name}${c.platform_display ? ` (${c.platform_display})` : ''}`,
    }),
  },
};

export const ENGAGE_SOURCE_OPTIONS = [
  'phone', 'whatsapp', 'website', 'walk_in',
  'staff_referral', 'doctor_referral', 'ambulance_referral',
  'health_camp', 'corporate_account', 'insurance',
  'digital_marketing', 'word_of_mouth', 'emergency_108',
  'social_media', 'other',
];
